"use client";

import Link from "next/link";
import Image from "next/image";
import {
  Server,
  Network,
  Layers,
  Boxes,
  ShieldCheck,
  Building2,
  Wallet,
  ArrowLeftRight,
  Rocket,
  Zap,
  Database,
  BookOpen,
  Newspaper,
  Award,
  Activity,
  Scale,
  Gift,
  Coins,
  Bot,
  CandlestickChart,
  ArrowRight,
  ChevronRight,
  type LucideIcon,
} from "lucide-react";
import { type NavGroup, type NavItem } from "@/data/navigation";

interface MegaMenuProps {
  group: NavGroup;
  isOpen: boolean;
}

/* String keys in the nav data → lucide icons. Keeps the data file free of
   component imports so content edits stay declarative. */
const ICONS: Record<string, LucideIcon> = {
  server: Server,
  network: Network,
  layers: Layers,
  boxes: Boxes,
  shield: ShieldCheck,
  building: Building2,
  wallet: Wallet,
  swap: ArrowLeftRight,
  rocket: Rocket,
  zap: Zap,
  database: Database,
  book: BookOpen,
  news: Newspaper,
  award: Award,
  activity: Activity,
  compare: Scale,
  gift: Gift,
  bot: Bot,
  chart: CandlestickChart,
};

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded bg-brand-purple/15 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-brand-purple-light">
      {children}
    </span>
  );
}

/* ── Item leading visual: chain logo, mapped icon, or a neutral fallback ── */
function ItemVisual({ item }: { item: NavItem }) {
  if (item.logoSrc) {
    return (
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-bg-base transition-colors duration-150 group-hover:border-brand-purple/40">
        <Image
          src={item.logoSrc}
          alt=""
          width={20}
          height={20}
          className="h-5 w-5 object-contain"
        />
      </span>
    );
  }

  const Icon = (item.icon && ICONS[item.icon]) || Coins;
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border-subtle bg-bg-base text-text-secondary transition-colors duration-150 group-hover:border-brand-purple/40 group-hover:text-brand-purple-light">
      <Icon size={17} strokeWidth={1.75} />
    </span>
  );
}

/* ── A single row/card in a "list" layout menu ─────────────────────────── */
function MenuItem({ item }: { item: NavItem }) {
  return (
    <div>
      <Link
        href={item.href}
        className="group flex items-start gap-3 rounded-xl p-3 transition-colors duration-150 hover:bg-white/4"
      >
        <ItemVisual item={item} />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-sm font-medium text-text-primary transition-colors duration-150 group-hover:text-white">
              {item.label}
            </span>
            {item.badge && <Badge>{item.badge}</Badge>}
          </span>
          {item.description && (
            <span className="mt-0.5 block truncate text-xs leading-relaxed text-text-muted">
              {item.description}
            </span>
          )}
        </span>
      </Link>

      {/* Nested sub-links (e.g. validator app CTAs) */}
      {item.children && item.children.length > 0 && (
        <div className="ml-13 mt-0.5 space-y-0.5 border-l border-border-subtle/60 pl-3">
          {item.children.map((child) => (
            <Link
              key={child.href}
              href={child.href}
              className="group flex items-center gap-1.5 rounded-md py-1.5 pl-1 pr-2 text-xs text-text-secondary transition-colors duration-150 hover:text-white"
            >
              <ChevronRight
                size={12}
                className="text-text-muted transition-transform duration-150 group-hover:translate-x-0.5"
              />
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Compact centred tile for the "logos" layout (Chains) ──────────────── */
function LogoTile({ item }: { item: NavItem }) {
  return (
    <Link
      href={item.href}
      className="group flex flex-col items-center gap-2 rounded-xl border border-transparent p-3 text-center transition-colors duration-150 hover:border-border-subtle hover:bg-white/4"
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-border-subtle bg-bg-base transition-colors duration-150 group-hover:border-brand-purple/40">
        {item.logoSrc ? (
          <Image
            src={item.logoSrc}
            alt=""
            width={22}
            height={22}
            className="h-5.5 w-5.5 object-contain"
          />
        ) : (
          <Coins size={20} className="text-text-secondary" />
        )}
      </span>
      <span className="text-xs font-medium text-text-secondary transition-colors duration-150 group-hover:text-white">
        {item.label}
      </span>
    </Link>
  );
}

/* ── Monospace category eyebrow (// INFRASTRUCTURE) ────────────────────── */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 flex items-center gap-2 px-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-text-muted">
      <span className="h-px w-4 shrink-0 bg-brand-purple/60" aria-hidden />
      {children}
    </p>
  );
}

/* ── Right-hand CTA rail ───────────────────────────────────────────────── */
function CtaRail({ cta }: { cta: NonNullable<NavGroup["cta"]> }) {
  return (
    <div className="flex w-60 shrink-0 flex-col justify-center gap-4 border-l border-border-subtle bg-linear-to-b from-brand-purple/10 to-transparent p-6">
      {cta.eyebrow && (
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-brand-purple-light">
          {cta.eyebrow}
        </span>
      )}
      <p className="text-xl font-semibold leading-tight text-text-primary">
        {cta.title}
      </p>
      {cta.description && (
        <p className="text-xs text-text-muted">{cta.description}</p>
      )}
      <Link
        href={cta.href}
        {...(cta.external
          ? { target: "_blank", rel: "noopener noreferrer" }
          : {})}
        data-cta="deploy-now"
        data-cta-location="mega-menu"
        className="mt-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent px-4 py-2.5 text-sm font-semibold text-white transition-[filter,transform] duration-150 hover:-translate-y-px hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple/60"
      >
        {cta.buttonLabel}
        <ArrowRight size={15} />
      </Link>
    </div>
  );
}

export default function MegaMenu({ group, isOpen }: MegaMenuProps) {
  if (!isOpen) return null;

  const hasSections = !!group.sections?.length;
  const columns = group.columns ?? 2;
  const isLogos = group.layout === "logos";

  return (
    // `pt-2` (not margin) keeps the hover bridge continuous between the
    // trigger and the panel so the menu doesn't close crossing the gap.
    <div className="absolute left-0 top-full z-50 pt-2">
      <div
        role="menu"
        className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-surface shadow-[0_20px_60px_rgba(0,0,0,0.55)]"
        style={{ width: group.width ?? 640, maxWidth: "calc(100vw - 2rem)" }}
      >
        <div className="flex">
          {/* Main content area */}
          <div className="min-w-0 flex-1 p-3">
            {hasSections ? (
              <div
                className="grid gap-x-4 gap-y-5"
                style={{
                  gridTemplateColumns: `repeat(${group.sections!.length}, minmax(0, 1fr))`,
                }}
              >
                {group.sections!.map((section, i) => (
                  <div
                    key={section.title}
                    className={
                      i > 0 ? "border-l border-border-subtle/60 pl-3" : ""
                    }
                  >
                    <SectionLabel>{section.title}</SectionLabel>
                    <div className="space-y-0.5">
                      {section.items.map((item) => (
                        <MenuItem key={item.href + item.label} item={item} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {group.heading && <SectionLabel>{group.heading}</SectionLabel>}
                <div
                  className={isLogos ? "grid gap-1" : "grid gap-0.5"}
                  style={{
                    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                  }}
                >
                  {group.items?.map((item) =>
                    isLogos ? (
                      <LogoTile key={item.href} item={item} />
                    ) : (
                      <MenuItem key={item.href} item={item} />
                    ),
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Optional CTA rail */}
          {group.cta && <CtaRail cta={group.cta} />}
        </div>

        {/* Footer bar — quick links + optional "view all" */}
        {(group.footer?.length || group.viewAllHref) && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-t border-border-subtle bg-bg-base/40 px-4 py-3">
            {group.viewAllHref && (
              <Link
                href={group.viewAllHref}
                className="group inline-flex items-center gap-1 text-sm font-semibold text-brand-purple-light transition-colors duration-150 hover:text-white"
              >
                {group.viewAllLabel ?? "View all"}
                <ArrowRight
                  size={13}
                  className="transition-transform duration-150 group-hover:translate-x-0.5"
                />
              </Link>
            )}
            {group.footer?.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="group inline-flex items-center gap-1 text-sm font-semibold text-brand-purple-light transition-colors duration-150 hover:text-white"
              >
                {link.label}
                <ChevronRight
                  size={13}
                  className="transition-transform duration-150 group-hover:translate-x-0.5"
                />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
